/**
 * Relative gallery paths for a product row (JSON `product_images` or legacy `product_image`).
 * @param {{ product_images?: string | null, product_image?: string | null }} product
 * @returns {string[]}
 */
export function getProductGalleryPaths(product) {
  if (!product) return [];
  if (product.product_images) {
    try {
      const arr = JSON.parse(product.product_images);
      if (Array.isArray(arr) && arr.length) {
        const out = arr
          .filter((p) => typeof p === "string" && p.trim())
          .map((p) => p.trim().replace(/\\/g, "/"));
        if (out.length) return [...new Set(out)];
      }
    } catch (_err) {
      /* fall through */
    }
  }
  return parseFormImageList(product.product_image);
}

/**
 * Coerce store `formImages` from API (string, double-encoded JSON, object) to string paths.
 */
export function parseFormImageList(raw) {
  let v = raw;
  if (v == null || v === "") return [];
  if (typeof v === "string") {
    try {
      v = JSON.parse(v);
    } catch (_err) {
      return v.trim() ? [v.trim()] : [];
    }
    if (typeof v === "string") {
      try {
        v = JSON.parse(v);
      } catch (_err2) {
        return v.trim() ? [v.trim()] : [];
      }
    }
  }
  if (!Array.isArray(v)) {
    if (v && typeof v === "object") return parseFormImageList(Object.values(v));
    return [];
  }
  return v
    .map((item) => {
      if (typeof item === "string") return item.trim();
      if (item && typeof item === "object") {
        if (typeof item.path === "string") return item.path.trim();
        if (typeof item.url === "string") return item.url.trim();
      }
      return "";
    })
    .filter(Boolean);
}

/**
 * Build absolute URL for static files served by the API (Images/, etc.).
 * Normalizes Windows-style paths and bare `Store/...` legacy paths.
 */
export function apiAssetUrl(baseUrl, relativePath) {
  if (relativePath == null || relativePath === "") return "";
  const s = String(relativePath).trim();
  if (/^https?:\/\//i.test(s)) return s;
  const base = (baseUrl || "").replace(/\/+$/, "");
  let rel = s.replace(/\\/g, "/").replace(/^\/+/, "");
  const lower = rel.toLowerCase();
  if (!lower.startsWith("images/") && /^store\//i.test(rel)) {
    rel = `Images/${rel}`;
  }
  if (!base) return "";
  return `${base}/${rel}`;
}

/** When REACT_APP_URL is unset, use this public API (avoids surprise localhost in `npm start`). */
const PUBLIC_API_FALLBACK = "https://filmlocation.100billion.in";

/**
 * API origin for fetch() and image URLs.
 * 1. REACT_APP_URL when set — always wins (live/staging/local); strip trailing slashes.
 * 2. REACT_APP_USE_LOCAL_API=true → REACT_APP_LOCAL_API_URL or http://localhost:8000 (only when URL is unset).
 * 3. Production build in browser: same-origin (only if API is served from the site origin).
 * 4. Development on localhost with no URL → http://localhost:8000 (avoids POSTing to CRA :3000).
 * 5. Otherwise PUBLIC_API_FALLBACK.
 */
export const getApiBaseUrl = () => {
  const trimmed = (process.env.REACT_APP_URL || "").trim().replace(/\/+$/, "");
  if (trimmed) return trimmed;
  if (process.env.REACT_APP_USE_LOCAL_API === "true") {
    return (process.env.REACT_APP_LOCAL_API_URL || "http://localhost:8000")
      .trim()
      .replace(/\/+$/, "");
  }
  if (
    process.env.NODE_ENV !== "development" &&
    typeof window !== "undefined" &&
    window.location &&
    window.location.origin
  ) {
    return window.location.origin;
  }
  if (
    typeof window !== "undefined" &&
    /^localhost$|^127\.0\.0\.1$/i.test(window.location.hostname || "")
  ) {
    return "https://filmlocation.100billion.in";
  }
  return PUBLIC_API_FALLBACK;
};

/**
 * Base URL for location-permission API (create/upload/update).
 * Backend mounts the same router at /permission and /api/permission.
 * Default is /api/permission — on many hosts /permission/* is handled by static/SPA (404 HTML),
 * while /api/* is proxied to Node. Override: REACT_APP_PERMISSION_API_ROOT=/permission
 */
export const getPermissionApiBase = () => {
  const host = getApiBaseUrl().replace(/\/+$/, "");
  let root = (process.env.REACT_APP_PERMISSION_API_ROOT || "/api/permission").trim();
  if (!root.startsWith("/")) root = `/${root}`;
  root = root.replace(/\/+$/, "") || "/api/permission";
  return `${host}${root}`;
};

/** Parse fetch response as JSON; clear error if server returned HTML (wrong host / 404 SPA). */
export async function fetchApiJson(url, options) {
  let res;
  try {
    res = await fetch(url, options);
  } catch (e) {
    const raw = e && e.message ? String(e.message) : String(e);
    const hint =
      raw === "Failed to fetch"
        ? " Cannot reach the API — confirm REACT_APP_URL (wins over USE_LOCAL_API), restart `npm start` after .env changes, check CORS on the server, or disable extensions that block requests."
        : "";
    throw new Error(hint ? `${raw}.${hint}` : raw);
  }
  const text = await res.text();
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (!ct.includes("application/json")) {
    const hint =
      text.trimStart().startsWith("<!") || text.includes("<!DOCTYPE")
        ? " — received HTML (wrong API URL or route not deployed on server)."
        : "";
    throw new Error(
      `Not JSON (${res.status})${hint} Request: ${url.slice(0, 80)}`
    );
  }
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON from ${url.slice(0, 60)}…`);
  }
  return { res, data };
}
